import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Table, Button, Typography, Tooltip } from 'antd';
import { currencyFormatter } from '../../../utils/utils';
import dayjs from 'dayjs';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useGlobalContext } from '../../../utils/globalContext';
import { apiBasePath } from '../../../utils/constants';
import { normalizeNotificationData } from './normalize';

const { Text } = Typography;

export const NotificationModal = ({ visible, onClose, onErrorsFetched }) => {
  const { user } = useGlobalContext();
  const location = useLocation();

  const [countdown, setCountdown] = useState(5);
  const [acceptEnabled, setAcceptEnabled] = useState(false);
  const [invoiceErrors, setInvoiceErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const lastFetchTime = useRef(0);

  const onErrorsFetchedRef = useRef(onErrorsFetched);
  useEffect(() => {
    onErrorsFetchedRef.current = onErrorsFetched;
  }, [onErrorsFetched]);

  const fetchInvoiceErrors = useCallback(async (showLoading = false) => {
    if (!user || !user.clie_id || user.isAdmin) return;

    if (showLoading) {
      setLoading(true);
    }

    try {
      const response = await axios.get(
        `${apiBasePath}/invoice/stamped/clie_id/${user.clie_id}/stamped/0`
        //?from_oper_fecha=${dayjs()
        //  .subtract(1, 'month')
        //  .startOf('month')
        //  .format('YYYY-MM-DD')}&to_oper_fecha=${dayjs().format('YYYY-MM-DD')}
      );

      if (response.data && response.data.listInvoice) {
        const normalizedData = normalizeNotificationData(response.data.listInvoice);
        setInvoiceErrors(normalizedData);
        if (onErrorsFetchedRef.current) {
          onErrorsFetchedRef.current(normalizedData);
        }
      } else {
        setInvoiceErrors([]);
        if (onErrorsFetchedRef.current) {
          onErrorsFetchedRef.current([]);
        }
      }
      lastFetchTime.current = Date.now();
    } catch (error) {
      console.error('Error fetching invoice errors:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [user]);

  // Silent fetch on mount, user change, or route change
  useEffect(() => {
    if (user && user.clie_id && !user.isAdmin && location.pathname === '/') {
      fetchInvoiceErrors(false);
    }
  }, [user, location.pathname, fetchInvoiceErrors]);

  // Fetch with loader when modal opens
  useEffect(() => {
    if (visible) {
      // Avoid double fetch if we just did a silent fetch in the last 2 seconds
      if (Date.now() - lastFetchTime.current > 2000) {
        fetchInvoiceErrors(true);
      }
    }
  }, [visible, fetchInvoiceErrors]);

  // Countdown timer logic
  useEffect(() => {
    let timer;
    if (visible && !loading) {
      setCountdown(5);
      setAcceptEnabled(false);

      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setAcceptEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (visible && loading) {
      setCountdown(5);
      setAcceptEnabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [visible, loading]);

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      width: 70,
      render: (text) => (text ? dayjs(text).format('DD/MM/YYYY') : '--'),
    },
    {
      title: 'Número Factura',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 100,
      render: (text) => text || '--',
    },
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 180,
      render: (text) => text || '--',
    },
    {
      title: 'Tipo de Factura',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      width: 100,
      render: (text) => text || '--',
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount) => currencyFormatter(amount),
    },
    {
      title: 'Error',
      dataIndex: 'errorDescription',
      key: 'errorDescription',
      width: 200,
      render: (text) => {
        return (
          <>
            {text ? (
              text.includes('exitoso') ? null : (
                <Tooltip title={text}>
                  <div style={{ cursor: 'pointer' }} className="texto-recortado-md">
                    {text}
                  </div>
                </Tooltip>
              )
            ) : null}
          </>
        );
      },
    },
  ];

  return (
    <Modal
      title="Facturas con Error"
      open={visible}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={800}
      footer={[
        <Button
          key="submit"
          type="primary"
          disabled={!acceptEnabled}
          onClick={onClose}
        >
          {acceptEnabled ? 'Aceptar' : `Aceptar (${countdown}s)`}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text>
          Se encontraron facturas con problemas en su emisión. Por favor, revise los detalles a continuación:
        </Text>
      </div>
      <Table
        dataSource={invoiceErrors}
        columns={columns}
        pagination={{ pageSize: 5 }}
        rowKey="id"
        size="middle"
        loading={loading}
      />
    </Modal>
  );
};
